import * as R from 'remeda'
import { FetchedInventory } from '~/features/hooks/useGetInventory'
import { InvalidStoryousItem, StoryousItem } from '~/features/storyous/hooks/useStoryous'
import { generateStockCode } from '~/utils/shared'

export type WarningId =
  | 'price-mismatch'
  | 'quantity-mismatch'
  | 'path-mismatch'
  | 'missing-item'
  | 'extra-item'

export type Warnings = Array<{ stock_code: string; warning: string }>

export const getStoryousWarnings = ({
  storyousModels: { valid, invalid },
  inventory,
}: {
  storyousModels: { valid: StoryousItem[]; invalid: InvalidStoryousItem[] }
  inventory: FetchedInventory[] | undefined
}) => {
  if (!inventory) return []

  const normalizedInventory = inventory.map((item) => ({
    price: item.models_v2.price,
    size: item.size,
    store: item.location,
    name: generateStockCode({
      size: item.size,
      setId: item.models_v2.set_id,
      material: item.models_v2.material,
      product_type: item.models_v2.product_type,
    }),
  }))

  const storyousGroup = R.mapValues(
    R.groupBy(valid, (inv) => inv.name),
    (item) => R.mapToObj(item, (el) => [el.store, el])
  )
  const inventoryGroup = R.groupBy(normalizedInventory, (item) => item.name)
  const storyousMap = R.mapToObj(valid, (item) => [item.name, item])

  const invalidMap = R.groupBy(invalid, (inv) => inv.name)

  // check if item has validation errors in storyous (broken items)
  const brokenItemsInStoryous = normalizedInventory.flatMap(({ name }) => {
    if (storyousMap[name]) return [] // Item exists in valid data
    const invalids = invalidMap[name]

    // Only create broken warning if there are actual invalid entries
    if (!invalids || invalids.length === 0) return []

    const invalidMessages = invalids
      .map((el) => el.message)
      .filter(Boolean)
      .join(', ')

    return [
      {
        name,
        store: '',
        price: 0,
        warning: `Item is broken in Storyous - `.concat(invalidMessages),
        amount: 0,
        warningId: 'path-mismatch' as const,
      },
    ]
  })

  // check if item is completely missing in storyous (no valid, no invalid)
  const missingItemsInStoryous = normalizedInventory.flatMap(({ name }) => {
    if (storyousMap[name]) return [] // Item exists in valid data
    if (invalidMap[name] && invalidMap[name].length > 0) return [] // Item has invalid entries (broken)

    return [
      {
        name,
        store: '',
        price: 0,
        warning: `Item is missing in Storyous`,
        amount: 0,
        warningId: 'missing-item' as const,
      },
    ]
  })

  // check if item exists in storyous but is missing in inventory
  const extraItemsInStoryous = Object.keys(storyousGroup).flatMap((name) => {
    if (inventoryGroup[name]) return []

    const storeEntries = storyousGroup[name]
    if (!storeEntries) return []

    return R.pipe(
      storeEntries,
      R.values(),
      R.map((value) =>
        value.amount === 0
          ? null
          : {
              name: value.name,
              store: value.store,
              price: value.price,
              warning: 'Item exists in Storyous but is missing in Admin',
              amount: value.amount,
              warningId: 'extra-item' as const,
            }
      ),
      R.filter(R.isNonNullish)
    )
  })

  // check quantity inconsistencies
  const quantityInconsistencies = Object.keys(storyousGroup).flatMap((name) => {
    const variants = inventoryGroup[name]
    const storyousVariants = storyousGroup[name]
    if (!variants || !storyousVariants) return []
    const [mandarinVariants, wollemVariants] = R.partition(variants, (v) => v.store === 'mandarin')
    // fixes the issue: if both mandarin AND wollem have quantity inconsistencies for the same model, only the mandarin warning will be returned
    const warnings = []
    if (
      typeof storyousVariants.mandarin?.amount === 'number' &&
      mandarinVariants.length !== storyousVariants.mandarin.amount
    ) {
      warnings.push({
        ...storyousVariants.mandarin,
        amount: storyousVariants.mandarin.amount,
        store: 'mandarin',
        warning: `Quantity in Mandarin of Admin is ${mandarinVariants.length} but in Storyous is ${storyousVariants.mandarin.amount}`,
        warningId: 'quantity-mismatch' as const,
      })
    }

    if (
      typeof storyousVariants.wollem?.amount === 'number' &&
      wollemVariants.length !== storyousVariants.wollem.amount
    ) {
      warnings.push({
        ...storyousVariants.wollem,
        amount: storyousVariants.wollem.amount,
        store: 'wollem',
        warning: `Quantity in Wollem of Admin is ${wollemVariants.length} but in Storyous is ${storyousVariants.wollem.amount}`,
        warningId: 'quantity-mismatch' as const,
      })
    }

    return warnings
  })

  const priceInconsistencies = Object.keys(storyousGroup).flatMap((name) => {
    const variants = inventoryGroup[name]
    const storyousVariants = storyousGroup[name]
    if (!variants || !storyousVariants) return []

    const [wollemVariants, mandarinVariants] = R.partition(variants, (v) => v.store === 'wollem')

    const wollemPriceList = R.pipe(
      wollemVariants,
      R.filter(
        (v) => !!storyousVariants.wollem?.price && v.price !== storyousVariants.wollem.price
      ),
      R.uniqueBy((v) => v.name)
    )
    const mandarinPriceList = R.pipe(
      mandarinVariants,
      R.filter(
        (v) => !!storyousVariants.mandarin?.price && v.price !== storyousVariants.mandarin.price
      ),
      R.uniqueBy((v) => v.name)
    )

    const warnings = []

    if (wollemPriceList.length > 0) {
      warnings.push(
        ...wollemPriceList.map((v) => ({
          ...storyousVariants.wollem,
          name,
          amount: storyousVariants.wollem.amount,
          price: storyousVariants.wollem?.price,
          store: 'wollem',
          warning: `Price in Wollem of Admin is ${v.price} but in Storyous is ${storyousVariants.wollem?.price}`,
          warningId: 'price-mismatch' as const,
        }))
      )
    }

    if (mandarinPriceList.length > 0) {
      warnings.push(
        ...mandarinPriceList.map((v) => ({
          ...storyousVariants.mandarin,
          name,
          amount: storyousVariants.mandarin.amount,
          price: storyousVariants.mandarin?.price,
          store: 'mandarin',
          warning: `Price in Mandarin of Admin is ${v.price} but in Storyous is ${storyousVariants.mandarin?.price}`,
          warningId: 'price-mismatch' as const,
        }))
      )
    }

    return warnings
  })

  return [
    ...brokenItemsInStoryous,
    ...missingItemsInStoryous,
    ...extraItemsInStoryous,
    ...quantityInconsistencies,
    ...priceInconsistencies,
  ].map((item, index) => ({ ...item, id: index }))
}
